import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";

/**
 * Search result item
 */
interface SearchResult {
  id: string;
  type: "user" | "child";
  title: string;
  subtitle: string;
  href: string;
}

/**
 * GlobalSearch - Typeahead search for users and children.
 *
 * Features:
 * - Search by email, name, or ID
 * - Typeahead suggestions with keyboard navigation
 * - Quick jump to user/child detail
 * - Debounced search queries
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search function
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 2 || !supabase) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const searchResults: SearchResult[] = [];

      // Search profiles (users) by email
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, email")
        .ilike("email", `%${searchQuery}%`)
        .limit(5);

      if (!profilesError && profiles) {
        for (const profile of profiles) {
          const profileData = profile as { id: string; email: string | null };
          searchResults.push({
            id: profileData.id,
            type: "user",
            title: profileData.email || "Unknown User",
            subtitle: `User ID: ${profileData.id.substring(0, 8)}...`,
            href: `/admin/users/${profileData.id}`,
          });
        }
      }

      // Search children by display name
      const { data: children, error: childrenError } = await supabase
        .from("children")
        .select("id, display_name, profile_id")
        .ilike("display_name", `%${searchQuery}%`)
        .limit(5);

      if (!childrenError && children) {
        for (const child of children) {
          const childData = child as { id: string; display_name: string | null; profile_id: string };
          searchResults.push({
            id: childData.id,
            type: "child",
            title: childData.display_name || "Unknown Child",
            subtitle: `Child ID: ${childData.id.substring(0, 8)}...`,
            href: `/admin/children/${childData.id}`,
          });
        }
      }

      // Also search by exact ID match
      if (searchQuery.length >= 8) {
        // Check if it's a profile ID
        const { data: profileById, error: profileByIdError } = await supabase
          .from("profiles")
          .select("id, email")
          .eq("id", searchQuery)
          .maybeSingle();

        if (!profileByIdError && profileById) {
          const profileData = profileById as { id: string; email: string | null };
          if (!searchResults.some(r => r.id === profileData.id)) {
            searchResults.unshift({
              id: profileData.id,
              type: "user",
              title: profileData.email || "Unknown User",
              subtitle: `User ID: ${profileData.id.substring(0, 8)}...`,
              href: `/admin/users/${profileData.id}`,
            });
          }
        }

        // Check if it's a child ID
        const { data: childById, error: childByIdError } = await supabase
          .from("children")
          .select("id, display_name, profile_id")
          .eq("id", searchQuery)
          .maybeSingle();

        if (!childByIdError && childById) {
          const childData = childById as { id: string; display_name: string | null; profile_id: string };
          if (!searchResults.some(r => r.id === childData.id)) {
            searchResults.unshift({
              id: childData.id,
              type: "child",
              title: childData.display_name || "Unknown Child",
              subtitle: `Child ID: ${childData.id.substring(0, 8)}...`,
              href: `/admin/children/${childData.id}`,
            });
          }
        }
      }

      setResults(searchResults);
    } catch (error) {
      console.error("Search error:", error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.trim().length >= 2) {
      setIsLoading(true);
      debounceRef.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    } else {
      setResults([]);
      setIsLoading(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) {
      if (e.key === "Enter" && query.trim()) {
        // Navigate to search results page (future feature)
        return;
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle result selection
  const handleSelect = (result: SearchResult) => {
    navigate(result.href);
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Search input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="w-5 h-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setSelectedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search users, children..."
          className="
            w-full pl-10 pr-4 py-2
            bg-gray-100 border-0 rounded-lg
            text-sm text-gray-900 placeholder-gray-500
            focus:ring-2 focus:ring-magic-500 focus:bg-white
            transition-all duration-200
          "
          aria-label="Search"
          aria-expanded={isOpen}
          aria-controls="search-results"
          aria-autocomplete="list"
        />
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <LoadingSpinner />
          </div>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && (query.length >= 2 || results.length > 0) && (
        <div
          id="search-results"
          className="
            absolute top-full left-0 right-0 mt-2
            bg-white rounded-xl shadow-lg border border-gray-200
            max-h-96 overflow-y-auto
            z-50
          "
          role="listbox"
        >
          {isLoading && results.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              Searching...
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              No results found for "{query}"
            </div>
          ) : (
            <ul className="py-2">
              {results.map((result, index) => (
                <li key={`${result.type}-${result.id}`}>
                  <button
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3
                      text-left transition-colors
                      ${
                        selectedIndex === index
                          ? "bg-gray-50"
                          : "hover:bg-gray-50"
                      }
                    `}
                    role="option"
                    aria-selected={selectedIndex === index}
                  >
                    <div
                      className={`
                        w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                        ${
                          result.type === "user"
                            ? "bg-magic-100 text-magic-600"
                            : "bg-sunshine-100 text-sunshine-700"
                        }
                      `}
                    >
                      {result.type === "user" ? <UserIcon /> : <ChildIcon />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {result.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {result.subtitle}
                      </p>
                    </div>
                    <span
                      className={`
                        text-xs font-medium px-2 py-1 rounded-full
                        ${
                          result.type === "user"
                            ? "bg-magic-100 text-magic-700"
                            : "bg-sunshine-100 text-sunshine-700"
                        }
                      `}
                    >
                      {result.type === "user" ? "User" : "Child"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Keyboard hints */}
          {results.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 flex gap-4">
              <span>
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-gray-200 font-mono">↑↓</kbd>{" "}
                to navigate
              </span>
              <span>
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-gray-200 font-mono">↵</kbd>{" "}
                to select
              </span>
              <span>
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-gray-200 font-mono">esc</kbd>{" "}
                to close
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Icons
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
      <path
        fillRule="evenodd"
        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChildIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
      <path d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4z" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <svg
      className="animate-spin w-4 h-4 text-gray-400"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default GlobalSearch;
